import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import MainLayout from '@/components/layouts/MainLayout';
import { User, BarChart3, FileText, CreditCard, Settings, HelpCircle, Download, MessageSquare } from 'lucide-react';

const AuthorGuide: React.FC = () => {
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-green-600 mb-4">
            Author Guide
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Complete Guide for Authors
          </p>
          <Badge variant="outline" className="text-sm">
            Version 1.0
          </Badge>
        </div>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Quick Navigation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-2 text-sm">
                <li><a href="#author-overview" className="hover:underline text-green-600">• Author Overview</a></li>
                <li><a href="#author-dashboard" className="hover:underline text-green-600">• Author Dashboard</a></li>
                <li><a href="#view-royalties" className="hover:underline text-green-600">• Viewing Royalties</a></li>
                <li><a href="#request-payments" className="hover:underline text-green-600">• Requesting Payments</a></li>
              </ul>
              <ul className="space-y-2 text-sm">
                <li><a href="#payment-preferences" className="hover:underline text-green-600">• Payment Preferences</a></li>
                <li><a href="#get-support" className="hover:underline text-green-600">• Getting Support</a></li>
                <li><a href="#account-settings" className="hover:underline text-green-600">• Account Settings</a></li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Author Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="author-overview" className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Author Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              As an author, you can view your royalty reports, request payments, manage your 
              payment preferences, and get support when needed. The author portal provides 
              all the tools you need to track your earnings and manage your account.
            </p>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">What You Can Do</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="text-sm space-y-1">
                  <li>• View quarterly royalty reports</li>
                  <li>• Download PDF reports</li>
                  <li>• Request payments when eligible</li>
                </ul>
                <ul className="text-sm space-y-1">
                  <li>• Set up payment preferences</li>
                  <li>• Submit support tickets</li>
                  <li>• Update account settings</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Author Dashboard */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="author-dashboard" className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Author Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <h4 className="font-semibold mb-3">Understanding Your Dashboard:</h4>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Current Balance</p>
                  <p className="text-sm text-gray-600">Your available earnings ready for payment</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Recent Royalty Reports</p>
                  <p className="text-sm text-gray-600">Latest quarterly earnings breakdown</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Payment Eligibility</p>
                  <p className="text-sm text-gray-600">Check if you can request a payment</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium">Quick Actions</p>
                  <p className="text-sm text-gray-600">Access key features like payments and support</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Viewing Royalties */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="view-royalties" className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Viewing Royalty Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <h4 className="font-semibold mb-3">Accessing Your Reports:</h4>
            <div className="space-y-3 mb-6">
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Navigate to Royalty Reports</p>
                  <p className="text-sm text-gray-600">Go to Royalty Reports or use sidebar menu</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Browse Available Reports</p>
                  <p className="text-sm text-gray-600">See all your quarterly reports by year and quarter</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">View Report Details</p>
                  <p className="text-sm text-gray-600">Click on any report to see monthly breakdown</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium">Download PDF</p>
                  <p className="text-sm text-gray-600">Use the <Download className="h-4 w-4 inline" /> download button for PDF copy</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold mb-2 text-blue-800">Report Information Includes:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                <ul className="space-y-1">
                  <li>• Book title and ISBN</li>
                  <li>• Report period (quarter/year)</li>
                  <li>• Total units sold</li>
                </ul>
                <ul className="space-y-1">
                  <li>• Monthly sales breakdown</li>
                  <li>• Gross and net amounts</li>
                  <li>• Current status</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requesting Payments */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="request-payments" className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Requesting Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <h4 className="font-semibold mb-3">How to Request a Payment:</h4>
            <div className="space-y-3 mb-6">
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Check Eligibility</p>
                  <p className="text-sm text-gray-600">Ensure you meet the minimum payment threshold</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Go to Payments</p>
                  <p className="text-sm text-gray-600">Navigate to Payments section</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Click "Request Payment"</p>
                  <p className="text-sm text-gray-600">Use the request payment button if eligible</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium">Enter Amount</p>
                  <p className="text-sm text-gray-600">Specify how much you want to withdraw (up to your balance)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</div>
                <div>
                  <p className="font-medium">Submit Request</p>
                  <p className="text-sm text-gray-600">Review and submit for admin approval</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold mb-2 text-yellow-800">Payment Requirements:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Must have payment method set up</li>
                <li>• Balance must meet minimum threshold</li>
                <li>• Cannot have pending payment requests</li>
                <li>• Processing takes 3-5 business days</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Payment Preferences */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="payment-preferences" className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Payment Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <h4 className="font-semibold mb-3">Setting Up Payment Methods:</h4>
            <div className="space-y-3 mb-6">
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Access Payment Preferences</p>
                  <p className="text-sm text-gray-600">Go to Payment Preferences from sidebar</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Choose Payment Method</p>
                  <p className="text-sm text-gray-600">Select from PayPal, bank transfer, or check</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Enter Details</p>
                  <p className="text-sm text-gray-600">Provide required information for your chosen method</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium">Set Threshold</p>
                  <p className="text-sm text-gray-600">Choose minimum amount for automatic payments</p>
                </div>
              </div>
            </div>

            <h4 className="font-semibold mb-3">Available Payment Methods:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 border rounded-lg">
                <h5 className="font-medium mb-2">PayPal</h5>
                <p className="text-sm text-gray-600">Fast, secure payments to your PayPal account</p>
                <p className="text-xs text-gray-500 mt-1">Required: PayPal email address</p>
              </div>
              <div className="p-3 border rounded-lg">
                <h5 className="font-medium mb-2">Bank Transfer</h5>
                <p className="text-sm text-gray-600">Direct deposit to your bank account</p>
                <p className="text-xs text-gray-500 mt-1">Required: Account & routing numbers</p>
              </div>
              <div className="p-3 border rounded-lg">
                <h5 className="font-medium mb-2">International Transfer</h5>
                <p className="text-sm text-gray-600">Wire transfer for international authors</p>
                <p className="text-xs text-gray-500 mt-1">Required: SWIFT code, bank details</p>
              </div>
              <div className="p-3 border rounded-lg">
                <h5 className="font-medium mb-2">Check</h5>
                <p className="text-sm text-gray-600">Physical check mailed to your address</p>
                <p className="text-xs text-gray-500 mt-1">Required: Mailing address</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Getting Support */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="get-support" className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Getting Support
            </CardTitle>
          </CardHeader>
          <CardContent>
            <h4 className="font-semibold mb-3">Submitting Support Tickets:</h4>
            <div className="space-y-3 mb-6">
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Access Help Center</p>
                  <p className="text-sm text-gray-600">Go to Help Center from sidebar</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Check FAQs First</p>
                  <p className="text-sm text-gray-600">Browse common questions for quick answers</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Submit a Ticket</p>
                  <p className="text-sm text-gray-600">If you can't find an answer, create a support ticket</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium">Track Your Tickets</p>
                  <p className="text-sm text-gray-600">Monitor ticket status in My Tickets section</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-2">When to Contact Support</h4>
                <ul className="text-sm space-y-1">
                  <li>• Payment issues or delays</li>
                  <li>• Royalty report discrepancies</li>
                  <li>• Account access problems</li>
                  <li>• Technical issues</li>
                </ul>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold mb-2">Response Times</h4>
                <ul className="text-sm space-y-1">
                  <li>• General inquiries: 24-48 hours</li>
                  <li>• Payment issues: 12-24 hours</li>
                  <li>• Technical problems: 4-8 hours</li>
                  <li>• Urgent matters: 2-4 hours</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle id="account-settings" className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <h4 className="font-semibold mb-3">Managing Your Account:</h4>
            <div className="space-y-3 mb-6">
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Access Account Settings</p>
                  <p className="text-sm text-gray-600">Go to Account Settings from sidebar</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Update Profile Information</p>
                  <p className="text-sm text-gray-600">Change your name, email, or other details</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Change Password</p>
                  <p className="text-sm text-gray-600">Update your password for security</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium">Upload Avatar</p>
                  <p className="text-sm text-gray-600">Add a profile picture to personalize your account</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-semibold mb-2 text-red-800">Important Security Notes:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Always use a strong, unique password</li>
                <li>• Never share your login credentials</li>
                <li>• Log out when using shared computers</li>
                <li>• Contact support immediately if you suspect unauthorized access</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 border-t">
          <p className="text-gray-600 mb-2">
            For additional support, contact us at{' '}
            <a href="mailto:support@authorstranquilitypress.com" className="text-blue-600 hover:underline">
              support@authorstranquilitypress.com
            </a>
          </p>
          <p className="text-gray-600">
            Or call our 24/7 hotline: <span className="font-medium">+1 (866) 411 8655</span>
          </p>
          <p className="text-sm text-gray-500 mt-4">
            © 2025 Author's Tranquility Press - Author Guide | Powered Royalty Team
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default AuthorGuide;
